/**
 * Error Reporting Utility
 *
 * Provides centralized error reporting using Sentry.
 * Falls back gracefully if Sentry is not configured.
 *
 * Usage:
 *   import { reportError } from '@/lib/error-reporting'
 *   reportError(error, { context: 'ErrorBoundary' })
 */

import { captureException } from "@sentry/nextjs";

interface ErrorContext {
  /** Additional context about where the error occurred */
  context?: string;
  /** User-facing error message */
  message?: string;
  /** Error digest (Next.js error ID) */
  digest?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Report an error to Sentry
 *
 * In production:
 * - Reports to Sentry if NEXT_PUBLIC_SENTRY_DSN is configured
 * - Otherwise, logs to console.error
 *
 * In development:
 * - Always logs to console.error with full details
 * - Reports to Sentry only if NEXT_PUBLIC_SENTRY_DSN is explicitly set
 */
export function reportError(
  error: Error & { digest?: string },
  context?: ErrorContext,
): void {
  const isDevelopment = process.env.NODE_ENV === "development";
  const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

  // Always log to console in development
  if (isDevelopment) {
    console.error("Error reported:", {
      error,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      context: context?.context,
      metadata: context?.metadata,
    });
  }

  // Report to Sentry if configured
  if (sentryDsn || (!isDevelopment && typeof window !== "undefined")) {
    try {
      captureException(error, {
        tags: {
          errorBoundary: context?.context || "unknown",
          digest: error.digest,
        },
        extra: {
          message: context?.message,
          metadata: context?.metadata,
          stack: error.stack,
        },
        level: context?.context === "GlobalErrorBoundary" ? "fatal" : "error",
      });
    } catch (sentryError) {
      // Fallback if Sentry fails
      console.error("Error (Sentry reporting failed):", error.message, context);
      console.error("Sentry error:", sentryError);
    }
  } else if (!isDevelopment) {
    // No Sentry configured in production - log to console
    console.error("Error:", error.message, {
      digest: error.digest,
      context: context?.context,
      stack: error.stack,
    });
  }
}
